import { packageLabel, packageLabelWithPrice, PACKAGES, INCLUDED_ADDON_KEYS } from '@/lib/realEstatePackages';
import { IncludesList } from '@/components/booking/homeSteps';
import { WebsiteRequestNote } from '@/components/admin/dashboard/WebsiteRequests';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import DropZone from '@/components/DropZone';
import { ShareLinkCard } from '@/components/admin/ShareLinkCard';
import { shareLinkFor } from '@/lib/share';
import {
  Loader2, ArrowLeft, Home, User, Phone, Building2, Calendar, Clock, Key,
  Package, DollarSign, MessageSquare, Camera, Trash2, GripVertical, Star,
  Share2, Link2, Copy, Check, ExternalLink, Download, Lock, Unlock, Eye, Plus, X, Pencil, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { ScheduleShootModal } from '@/components/scheduling/ScheduleShootModal';
import { InvoiceSendControls } from '@/components/admin/InvoiceSendControls';
import { ChangePackageDialog } from '@/components/admin/ChangePackageDialog';
import { startZipDownload } from '@/lib/zipDownload';
import { ScheduledShootCard, useCalendarLinks } from '@/components/scheduling/AddToCalendar';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Status flow for real estate
const STATUS_FLOW = ['requested', 'scheduled', 'shot', 'editing', 'delivered', 'paid'];

const STATUS_CONFIG = {
  requested: { label: 'Requested', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  shot: { label: 'Shot', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  editing: { label: 'Editing', className: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  paid: { label: 'Paid', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  canceled: { label: 'Canceled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
};


function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPrice(cents) {
  if (!cents) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Gallery state
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Custom price state
  const [customPrice, setCustomPrice] = useState('');
  const [settingPrice, setSettingPrice] = useState(false);

  // Share link state
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Invoice state
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [rebuildingInvoice, setRebuildingInvoice] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Invoice creation modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [invoiceAddons, setInvoiceAddons] = useState({});  // key -> { selected: bool, quantity: number }
  const [invoiceCustomItems, setInvoiceCustomItems] = useState([]);
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [changePkgOpen, setChangePkgOpen] = useState(false);

  const isAdmin = user?.roles?.includes('admin');

  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState(null); // 'schedule' | 'reschedule' | null
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSendEmail, setCancelSendEmail] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  const calendarLinks = useCalendarLinks(isAdmin ? orderId : null, calendarRefresh);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Admin-created orders open straight into the Schedule Shoot form (?schedule=1)
  useEffect(() => {
    if (searchParams.get('schedule') === '1' && order && calendarLinks && !calendarLinks.scheduled) {
      setScheduleMode('schedule');
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, order, calendarLinks]);

  // Portfolio state
  const [showInPortfolio, setShowInPortfolio] = useState(false);
  const [portfolioSaving, setPortfolioSaving] = useState(false);
  const [publicShowcaseUrl, setPublicShowcaseUrl] = useState('');
  const [clientLinkCopied, setClientLinkCopied] = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);

  // Fetch order
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}`);
      if (!response.ok) throw new Error('Failed to fetch order');
      const data = await response.json();
      setOrder(data);
      if (data.share_token && data.gallery_slug) {
        setShareUrl(`${window.location.origin}/property/${data.gallery_slug}?token=${data.share_token}`);
      }
      // Set portfolio state
      setShowInPortfolio(data.show_in_portfolio || false);
      if (data.gallery_slug) {
        setPublicShowcaseUrl(shareLinkFor(data.gallery_slug));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Fetch gallery photos
  const fetchPhotos = useCallback(async () => {
    if (!order?.gallery_slug) return;
    try {
      setPhotosLoading(true);
      const response = await authFetch(`${API_BASE}/api/gallery/property/${order.gallery_slug}`);
      if (response.ok) {
        const data = await response.json();
        setPhotos(data.photos || []);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setPhotosLoading(false);
    }
  }, [order?.gallery_slug]);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchOrder();
    }
  }, [isAuthenticated, isAdmin, fetchOrder]);

  useEffect(() => {
    if (order?.gallery_slug) {
      fetchPhotos();
    }
  }, [order?.gallery_slug, fetchPhotos]);

  // Fetch invoice for this order
  const fetchInvoice = useCallback(async () => {
    if (!orderId) return;
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/order/${orderId}`);
      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
        if (data.status === 'paid') {
          setOrder((prev) => (prev && !['paid', 'canceled'].includes(prev.status)
            ? { ...prev, status: 'paid', payment_state: 'paid', paid_at: prev.paid_at || data.paid_at }
            : prev));
        }
      } else if (response.status === 404) {
        setInvoice(null);
      } else {
        setInvoiceError(`Couldn't load the invoice (error ${response.status})`);
      }
    } catch (err) {
      console.error('Failed to fetch invoice:', err);
      setInvoiceError("Couldn't load the invoice. Check your connection and try again.");
    } finally {
      setInvoiceLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (isAuthenticated && isAdmin && orderId) {
      fetchInvoice();
    }
  }, [isAuthenticated, isAdmin, orderId, fetchInvoice]);

  // Fetch available add-ons
  const fetchAddons = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/settings/addons/enabled`);
      if (response.ok) {
        const data = await response.json();
        setAvailableAddons(data);
      }
    } catch (err) {
      console.error('Failed to fetch add-ons:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchAddons();
    }
  }, [isAuthenticated, isAdmin, fetchAddons]);

  // Open invoice creation modal
  const openInvoiceModal = (existing = null) => {
    const current = existing ? Object.fromEntries((existing.addons || []).map(a => [a.key, a])) : null;
    const addonSelections = {};
    availableAddons.forEach(addon => {
      const sel = current
        ? { selected: !!current[addon.key], quantity: current[addon.key]?.quantity || 1 }
        : { selected: (order?.details?.add_ons || []).includes(addon.key), quantity: 1 };
      addonSelections[addon.key] = { ...sel, wasSelected: sel.selected };
    });
    setInvoiceAddons(addonSelections);
    setInvoiceCustomItems(existing ? (existing.line_items || []).slice(1).map(i => ({
      description: i.description, price: (i.unit_price_cents / 100).toFixed(2), quantity: i.quantity || 1,
    })) : []);
    setInvoiceNotes(existing?.notes || '');
    setInvoiceModalOpen(true);
  };

  // Add custom line item
  const addCustomItem = () => {
    setInvoiceCustomItems([...invoiceCustomItems, { description: '', price: '', quantity: 1 }]);
  };

  // Remove custom line item
  const removeCustomItem = (index) => {
    setInvoiceCustomItems(invoiceCustomItems.filter((_, i) => i !== index));
  };

  // Update custom line item
  const updateCustomItem = (index, field, value) => {
    const updated = [...invoiceCustomItems];
    updated[index][field] = value;
    setInvoiceCustomItems(updated);
  };

  // Create invoice with add-ons and custom items
  const createInvoice = async () => {
    setCreatingInvoice(true);
    try {
      // Build add-ons array with quantities (new format)
      const addons = Object.entries(invoiceAddons)
        .filter(([key, val]) => val.selected)
        .map(([key, val]) => ({
          key,
          quantity: val.quantity || 1,
        }));
      
      // Build custom line items
      const lineItems = invoiceCustomItems
        .filter(item => item.description && item.price)
        .map(item => ({
          description: item.description,
          quantity: parseInt(item.quantity, 10) || 1,
          unit_price_cents: Math.round(parseFloat(item.price) * 100),
        }));
      
      const response = await authFetch(`${API_BASE}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          order_id: orderId,
          addons,
          line_items: lineItems,
          notes: invoiceNotes || null,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        fetchInvoice();
        throw new Error(data.detail || 'Failed to create invoice');
      }
      const data = await response.json();
      setInvoice(data);
      setInvoiceModalOpen(false);
      toast.success(invoice ? 'Draft invoice updated' : 'Invoice created as draft');
    } catch (err) {
      console.error('Failed to create invoice:', err);
      toast.error(err.message);
    } finally {
      setCreatingInvoice(false);
    }
  };

  const rebuildInvoice = async () => {
    if (!invoice) return;
    if (!window.confirm('Rebuild this draft from the order package? Custom items and add-on changes will be replaced.')) return;
    setRebuildingInvoice(true);
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoice.id}/rebuild`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to rebuild invoice');
      setInvoice(data);
      toast.success('Invoice rebuilt from the order package');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRebuildingInvoice(false);
    }
  };

  // Send invoice
  const sendInvoice = async () => {
    if (!invoice) return;
    setSendingInvoice(true);
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoice.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_email: true }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send invoice');
      }
      const data = await response.json();
      setInvoice(data);
      
      // Show detailed toast based on email status
      if (data.email_status) {
        if (data.email_status.success) {
          toast.success(`Invoice email sent to ${data.email_status.recipient}`, {
            description: `Message ID: ${data.email_status.message_id?.substring(0, 20)}...`
          });
        } else {
          toast.error(`Invoice saved, but the email to ${data.email_status.recipient} didn't send`, {
            description: data.email_status.error,
            duration: 10000,
          });
        }
      } else {
        toast.success('Invoice sent to customer');
      }
    } catch (err) {
      console.error('Failed to send invoice:', err);
      toast.error(err.message);
    } finally {
      setSendingInvoice(false);
    }
  };

  // Mark invoice as paid (manual)
  const markInvoicePaid = async () => {
    if (!invoice) return;
    try {
      const response = await authFetch(`${API_BASE}/api/invoices/${invoice.id}/mark-paid`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to mark paid');
      const data = await response.json();
      setInvoice(data);
      // Also refresh order to get updated status
      fetchOrder();
      toast.success('Invoice marked as paid');
    } catch (err) {
      console.error('Failed to mark paid:', err);
      toast.error(err.message);
    }
  };

  // Update status
  const onScheduleSaved = async () => {
    setCalendarRefresh((n) => n + 1);
    await fetchOrder();
  };

  const cancelSchedule = async () => {
    try {
      setCancelling(true);
      const response = await authFetch(`${API_BASE}/api/scheduling/orders/${orderId}/cancel-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_email: cancelSendEmail }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to cancel shoot');
      toast.success('Shoot cancelled — order moved back to Requested');
      setCancelOpen(false);
      await onScheduleSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (newStatus === 'scheduled') {
      setScheduleMode(calendarLinks?.scheduled ? 'reschedule' : 'schedule');
      return;
    }
    if (newStatus === 'requested' && calendarLinks?.scheduled) {
      setCancelOpen(true);
      return;
    }
    try {
      setUpdating(true);
      const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) throw new Error('Failed to update status');
      toast.success(`Status updated to ${newStatus}`);
      await fetchOrder();
      fetchInvoice();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // Set custom price
  const handleSetPrice = async () => {
    const cents = Math.round(parseFloat(customPrice) * 100);
    if (isNaN(cents) || cents <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    try {
      setSettingPrice(true);
      const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/set-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price_cents: cents }),
      });
      if (!response.ok) throw new Error('Failed to set price');
      toast.success('Custom price set');
      setCustomPrice('');
      await fetchOrder();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSettingPrice(false);
    }
  };

  // Mark as paid
  const handleMarkPaid = async () => {
    try {
      setUpdating(true);
      const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/mark-paid`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to mark as paid');
      toast.success('Order marked as paid');
      await fetchOrder();
      fetchInvoice();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // Generate share link
  const handleGenerateShareLink = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/share-link`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to generate share link');
      const data = await response.json();
      const fullUrl = `${window.location.origin}${data.share_url}`;
      setShareUrl(fullUrl);
      toast.success('Share link generated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Revoke share link
  const handleRevokeShareLink = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/share-link`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to revoke share link');
      setShareUrl(null);
      toast.success('Share link revoked');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Copy share link
  const handleCopyLink = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Copy client link (with token)
  const handleCopyClientLink = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setClientLinkCopied(true);
      toast.success('Client link copied!');
      setTimeout(() => setClientLinkCopied(false), 2000);
    }
  };

  // Copy public showcase link (no token, paid only)
  const handleCopyPublicLink = async () => {
    if (publicShowcaseUrl) {
      await navigator.clipboard.writeText(publicShowcaseUrl);
      setPublicLinkCopied(true);
      toast.success('Public link copied!');
      setTimeout(() => setPublicLinkCopied(false), 2000);
    }
  };

  // Toggle portfolio visibility
  const handleTogglePortfolio = async () => {
    if (!order) return;
    setPortfolioSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/portfolio/admin/orders/${order.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_in_portfolio: !showInPortfolio,
          portfolio_cover_id: order.portfolio_cover_id || null,
          featured: !!order.featured,
        }),
      });
      if (!response.ok) throw new Error('Failed to update portfolio settings');
      setShowInPortfolio(!showInPortfolio);
      toast.success(!showInPortfolio ? 'Added to portfolio!' : 'Removed from portfolio');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPortfolioSaving(false);
    }
  };

  // Upload photos via backend (chunked) - avoids browser-to-S3 CORS
  const handlePhotoUpload = async (files, onProgress) => {
    const readError = async (res, fallback) => {
      try { return (await res.json()).detail || fallback; } catch { return fallback; }
    };
    for (const file of files) {
      const initRes = await authFetch(`${API_BASE}/api/gallery/orders/${orderId}/photos/upload-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'image/jpeg', size_bytes: file.size }),
      });
      if (!initRes.ok) throw new Error(await readError(initRes, 'Failed to start upload'));
      const { photo_id, chunk_size, total_parts } = await initRes.json();

      for (let part = 1; part <= total_parts; part++) {
        const chunk = file.slice((part - 1) * chunk_size, part * chunk_size);
        const chunkRes = await authFetch(`${API_BASE}/api/gallery/photos/${photo_id}/chunk?part=${part}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: chunk,
        });
        if (!chunkRes.ok) throw new Error(await readError(chunkRes, `Upload failed (part ${part})`));
        onProgress?.(Math.round((part / total_parts) * 80));
      }

      const doneRes = await authFetch(`${API_BASE}/api/gallery/photos/${photo_id}/complete`, { method: 'POST' });
      if (!doneRes.ok) throw new Error(await readError(doneRes, 'Failed to process photo'));
      onProgress?.(100);
    }
    await fetchPhotos();
  };

  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      const response = await authFetch(`${API_BASE}/api/gallery/photos/${photoId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete photo');
      toast.success('Photo deleted');
      await fetchPhotos();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Set cover photo
  const handleSetCover = async (photoId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/gallery/photos/${photoId}/set-cover`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to set cover');
      toast.success('Cover photo set');
      await fetchPhotos();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  // Access denied
  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl text-white mb-4">Access Denied</h1>
        <Button asChild>
          <Link to="/">Return Home</Link>
        </Button>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl text-white mb-4">Error</h1>
        <p className="text-[#E8E4DE]/60 mb-6">{error}</p>
        <Button asChild variant="outline">
          <Link to="/admin/real-estate-orders">Back to Orders</Link>
        </Button>
      </div>
    );
  }

  // Order not loaded yet
  if (!order) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  const details = order.details || {};
  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.requested;
  const currentStatusIndex = STATUS_FLOW.indexOf(order.status);
  const isPaid = order.status === 'paid';
  
  // Safe property display
  const propertyStreet = details.property_street || '';
  const propertyUnit = details.property_unit || '';
  const propertyCity = details.property_city || '';
  const propertyState = details.property_state || '';
  const propertyZip = details.property_zip || '';

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Button
            asChild
            variant="ghost"
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/admin/real-estate-orders">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Orders
            </Link>
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-light text-white tracking-wide">
                  RE-{order.id.split('-')[0].toUpperCase()}
                </h1>
                <Badge className={statusConfig.className}>
                  {statusConfig.label}
                </Badge>
                {isPaid && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    <Unlock className="h-3 w-3 mr-1" />
                    Unlocked
                  </Badge>
                )}
              </div>
              <p className="text-[#E8E4DE]/60">
                {order.title || 'Real Estate Order'} • Created {formatDate(order.created_at)}
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              {order.gallery_slug && (
                <Button
                  asChild
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Link to={`/property/${order.gallery_slug}`} target="_blank">
                    <Eye className="h-4 w-4 mr-1" />
                    View Gallery
                  </Link>
                </Button>
              )}
              {!isPaid && (
                <Button
                  onClick={handleMarkPaid}
                  disabled={updating}
                  size="sm"
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="mark-paid-btn"
                >
                  {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Mark Paid'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <WebsiteRequestNote orderId={orderId} />
        {order?.gallery_slug && (
          <Link to={`/listing-editor/${order.gallery_slug}`} className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-[#C6A14A]/40 text-[#C6A14A] text-sm hover:bg-[#C6A14A]/10" data-testid="admin-listing-editor-link">
            Listing page editor
          </Link>
        )}
        {/* Status Flow */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white">Order Status</CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Update the order status through the workflow
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {STATUS_FLOW.map((status, index) => {
                const config = STATUS_CONFIG[status];
                const isActive = status === order.status;
                const isPast = index < currentStatusIndex;
                const isNext = index === currentStatusIndex + 1;

                return (
                  <React.Fragment key={status}>
                    <Button
                      onClick={() => updateStatus(status)}
                      disabled={updating || isActive}
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      className={`
                        shrink-0
                        ${isActive ? 'bg-[#C6A14A] text-[#050816]' :
                          isPast ? 'border-green-500/30 text-green-400 bg-green-500/10' :
                          isNext ? 'border-[#C6A14A]/50 text-[#C6A14A]' :
                          'border-white/10 text-[#E8E4DE]/50'}
                      `}
                      data-testid={`status-${status}`}
                    >
                      {config.label}
                    </Button>
                    {index < STATUS_FLOW.length - 1 && (
                      <div className={`w-4 h-0.5 shrink-0 ${isPast ? 'bg-green-500/50' : 'bg-white/10'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {calendarLinks?.scheduled ? (
          <ScheduledShootCard
            links={calendarLinks}
            prefix="admin-calendar"
            showAdmin
            actions={
              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                <Button size="sm" variant="outline" onClick={() => setScheduleMode('reschedule')}
                  className="border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="reschedule-shoot-btn">
                  <Clock className="h-4 w-4 mr-1.5" /> Reschedule
                </Button>
                <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)}
                  className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10" data-testid="cancel-shoot-btn">
                  <X className="h-4 w-4 mr-1.5" /> Cancel Shoot
                </Button>
              </div>
            }
          />
        ) : order.status === 'requested' && (
          <Button onClick={() => setScheduleMode('schedule')}
            className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="open-schedule-modal-btn">
            <Calendar className="h-4 w-4 mr-2" /> Schedule Shoot
          </Button>
        )}

        <ScheduleShootModal
          open={!!scheduleMode}
          onOpenChange={(o) => !o && setScheduleMode(null)}
          orderId={orderId}
          mode={scheduleMode || 'schedule'}
          initial={scheduleMode === 'reschedule' && calendarLinks?.scheduled ? {
            date: calendarLinks.scheduled_start.slice(0, 10),
            time: calendarLinks.scheduled_start.slice(11, 16),
            duration: Math.round((new Date(calendarLinks.scheduled_end) - new Date(calendarLinks.scheduled_start)) / 60000),
          } : {
            date: details.preferred_date_1_date,
            timeWindow: details.preferred_date_1_time_window,
          }}
          onDone={onScheduleSaved}
        />

        <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
          <DialogContent className="bg-[#0f1724] border-white/10 text-white sm:max-w-md" data-testid="cancel-shoot-modal">
            <DialogHeader>
              <DialogTitle className="font-light tracking-wide">Cancel scheduled shoot?</DialogTitle>
              <DialogDescription className="text-[#E8E4DE]/60">
                The order goes back to Requested and a cancellation is sent to remove it from calendars.
              </DialogDescription>
            </DialogHeader>
            <label className="flex items-center gap-2 text-sm text-[#E8E4DE]">
              <Checkbox checked={cancelSendEmail} onCheckedChange={(v) => setCancelSendEmail(!!v)} data-testid="cancel-send-email-checkbox" />
              Email client & admin the cancellation
            </label>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelling}
                className="border-white/10 text-[#E8E4DE] bg-transparent hover:bg-white/5" data-testid="cancel-shoot-keep-btn">
                Keep Shoot
              </Button>
              <Button onClick={cancelSchedule} disabled={cancelling}
                className="bg-red-600 text-white hover:bg-red-700" data-testid="cancel-shoot-confirm-btn">
                {cancelling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Cancel Shoot
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Agent Info */}
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <User className="h-5 w-5 text-[#C6A14A]" />
                Agent Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-[#E8E4DE]/40" />
                <div>
                  <p className="text-xs text-[#E8E4DE]/50">Email</p>
                  <p className="text-white">{order.user_email || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#E8E4DE]/40" />
                <div>
                  <p className="text-xs text-[#E8E4DE]/50">Phone</p>
                  <p className="text-white">{order.user_phone || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="h-4 w-4 text-[#E8E4DE]/40" />
                <div>
                  <p className="text-xs text-[#E8E4DE]/50">Brokerage/Company</p>
                  <p className="text-white">{order.user_company || '—'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Property Info */}
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Home className="h-5 w-5 text-[#C6A14A]" />
                Property Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-[#E8E4DE]/50">Address</p>
                <p className="text-white">
                  {propertyStreet}
                  {propertyUnit && `, ${propertyUnit}`}
                </p>
                <p className="text-[#E8E4DE]/70">
                  {propertyCity}{propertyCity && propertyState ? ', ' : ''}{propertyState} {propertyZip}
                </p>
              </div>
              {order.service_type === 'vacant_land' ? (
                <div className="grid grid-cols-2 gap-4 text-sm" data-testid="land-details">
                  <div><p className="text-xs text-[#E8E4DE]/50">Type</p><p className="text-emerald-300">Vacant Land{order.payment_state === 'paid' ? ' · Paid upfront' : ''}</p></div>
                  <div><p className="text-xs text-[#E8E4DE]/50">Acreage</p><p className="text-white">{details.acreage ? `${details.acreage} acres · ` : ''}{details.land_band_label}</p></div>
                  <div><p className="text-xs text-[#E8E4DE]/50">City limits</p><p className="text-white">{{ inside: 'Inside Kingman', outside: 'Outside city limits', not_sure: 'Not sure (charged outside rate)' }[details.city_limits] || details.city_limits || '—'}</p></div>
                  <div><p className="text-xs text-[#E8E4DE]/50">APN</p><p className="text-white">{details.apn || '—'}</p></div>
                  <div><p className="text-xs text-[#E8E4DE]/50">Lot lines</p><p className="text-white">{details.lot_lines ? 'Yes (approximate)' : 'No'}</p></div>
                  <div><p className="text-xs text-[#E8E4DE]/50">Aerial video</p><p className="text-white">{details.add_ons?.includes('aerial_video_60s') ? 'Yes, 60 sec' : 'No'}</p></div>
                  <div className="col-span-2"><p className="text-xs text-[#E8E4DE]/50">On-site contact</p><p className="text-white">{[details.contact_name, details.contact_phone].filter(Boolean).join(' · ') || '—'}</p></div>
                </div>
              ) : (
              <div className="flex gap-6">
                <div>
                  <p className="text-xs text-[#E8E4DE]/50">Square Footage</p>
                  <p className="text-white">{details.square_footage ? `${details.square_footage.toLocaleString()} sq ft` : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#E8E4DE]/50">Occupancy</p>
                  <p className="text-white capitalize">{details.occupancy || '—'}</p>
                </div>
              </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Package & Pricing */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Package className="h-5 w-5 text-[#C6A14A]" />
              Package & Pricing
            </CardTitle>
            {['real_estate', 'vacant_land'].includes(order.service_type) && !invoiceLoading && (
              invoice || order.payment_state === 'paid' || order.status === 'paid' ? (
                <span className="flex items-center gap-1 text-xs text-[#E8E4DE]/50" data-testid="package-locked-note">
                  <Lock className="h-3 w-3" /> Locked — {invoice ? 'invoice created' : 'already paid'}
                </span>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setChangePkgOpen(true)} data-testid="change-package-btn"
                  className="border-[#C6A14A]/40 text-[#C6A14A] hover:bg-[#C6A14A]/10 bg-transparent">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Change package
                </Button>
              )
            )}
          </CardHeader>
          <ChangePackageDialog orderId={orderId} open={changePkgOpen} onOpenChange={setChangePkgOpen} onChanged={fetchOrder} />
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white font-medium">{order.service_type === 'vacant_land' ? `Vacant Land · ${details.land_band_label || ''}` : details.package_price_overridden ? `${packageLabel(details.package)} (custom price ${formatPrice(details.package_price_cents)})` : packageLabelWithPrice(details.package)}</p>
                {order.service_type !== 'vacant_land' && PACKAGES[details.package] && <IncludesList pkg={PACKAGES[details.package]} testId="admin-package-includes" />}
                {details.is_custom_quote && !details.total_price_cents && (
                  <p className="text-yellow-400 text-sm mt-1">Custom quote pending</p>
                )}
                {details.add_ons_price_cents > 0 && (
                  <p className="text-sm text-[#E8E4DE]/60 mt-1" data-testid="pricing-breakdown">
                    Package {formatPrice(details.package_price_cents)} + add-ons {formatPrice(details.add_ons_price_cents)}
                    {details.add_ons?.length ? ` (${details.add_ons.map((a) => a.replace(/_/g, ' ')).join(', ')})` : ''}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl text-[#C6A14A] font-light">
                  {formatPrice(details.total_price_cents)}
                </p>
              </div>
            </div>

            {/* Custom Price Setter */}
            {details.is_custom_quote && !details.total_price_cents && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <Label className="text-[#E8E4DE]/80 text-sm">Set Custom Price</Label>
                <div className="flex gap-2 mt-2">
                  <div className="relative flex-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#E8E4DE]/40" />
                    <Input
                      type="number"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      placeholder="0.00"
                      className="pl-9 bg-[#050816]/50 border-white/10 text-white"
                      data-testid="custom-price-input"
                    />
                  </div>
                  <Button
                    onClick={handleSetPrice}
                    disabled={settingPrice || !customPrice}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                  >
                    {settingPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Price'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Schedule & Access */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#C6A14A]" />
                Preferred Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-[#E8E4DE]/50">First Choice</p>
                <p className="text-white">
                  {details.preferred_date_1_date || '—'} • {details.preferred_date_1_time_window || '—'}
                </p>
              </div>
              {details.preferred_date_2_date && (
                <div>
                  <p className="text-xs text-[#E8E4DE]/50">Second Choice</p>
                  <p className="text-white">
                    {details.preferred_date_2_date} • {details.preferred_date_2_time_window}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Key className="h-5 w-5 text-[#C6A14A]" />
                Access Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-[#E8E4DE]/50">Lockbox Code</p>
                <p className="text-white font-mono text-lg">
                  {order.lockbox_code || '—'}
                </p>
              </div>
              {details.access_notes && (
                <div>
                  <p className="text-xs text-[#E8E4DE]/50">Access Notes</p>
                  <p className="text-white text-sm">{details.access_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Comments */}
        {details.comments && (
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[#C6A14A]" />
                Agent Comments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#E8E4DE]/80">{details.comments}</p>
            </CardContent>
          </Card>
        )}

        {/* Invoice Section - Show for ALL real estate orders */}
        {['real_estate', 'vacant_land'].includes(order.service_type) && (
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-[#C6A14A]" />
                    Invoice
                  </CardTitle>
                  {invoice && (
                    <CardDescription className="text-[#E8E4DE]/60">
                      {invoice.invoice_number} • ${(invoice.total_cents / 100).toFixed(2)}
                    </CardDescription>
                  )}
                </div>
                {invoice && (
                  <Badge className={
                    invoice.status === 'paid' 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : invoice.status === 'sent'
                      ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                      : 'bg-white/10 text-[#E8E4DE]'
                  }>
                    {invoice.status === 'paid' ? 'Paid' : invoice.status === 'sent' ? 'Sent' : 'Draft'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {invoiceLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 text-[#C6A14A] animate-spin" />
                </div>
              ) : invoiceError ? (
                <div className="text-center py-6" data-testid="invoice-load-error">
                  <p className="text-red-400 mb-4">{invoiceError}</p>
                  <Button variant="outline" className="border-white/20 text-white" onClick={fetchInvoice} data-testid="invoice-retry-btn">
                    <RefreshCw className="h-4 w-4 mr-1" /> Try again
                  </Button>
                </div>
              ) : invoice ? (
                <div className="space-y-4">
                  {/* Invoice Details */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#E8E4DE]/50">Subtotal:</span>
                      <span className="text-white ml-2">${(invoice.subtotal_cents / 100).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-[#E8E4DE]/50">Tax:</span>
                      <span className="text-white ml-2">${(invoice.tax_cents / 100).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-[#E8E4DE]/50">Total:</span>
                      <span className="text-[#C6A14A] ml-2 font-semibold">${(invoice.total_cents / 100).toFixed(2)}</span>
                    </div>
                    {invoice.status === 'paid' && invoice.discount_cents > 0 && (
                      <div data-testid="admin-invoice-discount">
                        <span className="text-[#E8E4DE]/50">Discount{invoice.discount_code ? ` (${invoice.discount_code})` : ''}:</span>
                        <span className="text-green-400 ml-2">-${(invoice.discount_cents / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {invoice.status === 'paid' && invoice.amount_paid_cents != null && (
                      <div data-testid="admin-invoice-amount-paid">
                        <span className="text-[#E8E4DE]/50">Paid:</span>
                        <span className="text-green-400 ml-2 font-semibold">${(invoice.amount_paid_cents / 100).toFixed(2)}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-[#E8E4DE]/50">Due:</span>
                      <span className="text-white ml-2">{invoice.due_date?.split('T')[0]}</span>
                    </div>
                  </div>
                  
                  {/* Line Items */}
                  {!invoice.line_items?.length && !invoice.addons?.length && (
                    <p className="text-sm text-amber-400" data-testid="invoice-empty-hint">This draft has no line items yet. Use “Rebuild from package” or Edit.</p>
                  )}
                  {(invoice.line_items?.length > 0 || invoice.addons?.length > 0) && (
                    <div className="border-t border-white/10 pt-4">
                      <h4 className="text-[#E8E4DE]/70 text-xs uppercase tracking-wider mb-2">Line Items</h4>
                      {invoice.line_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-[#E8E4DE]/80">{item.description}</span>
                          <span className="text-white">${((item.unit_price_cents * (item.quantity || 1)) / 100).toFixed(2)}</span>
                        </div>
                      ))}
                      {invoice.addons?.map((addon, idx) => (
                        <div key={`addon-${idx}`} className="flex justify-between text-sm py-1">
                          <span className="text-[#E8E4DE]/80">{addon.name}</span>
                          <span className="text-white">${((addon.price_cents * (addon.quantity || 1)) / 100).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {invoice.status !== 'draft' && (
                    <div className="pt-4 border-t border-white/10">
                      <InvoiceSendControls invoice={invoice} onUpdated={setInvoice} />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                    {invoice.status === 'draft' && (
                      <>
                        <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10"
                          onClick={() => openInvoiceModal(invoice)} data-testid="edit-invoice-btn">
                          <Pencil className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10"
                          onClick={rebuildInvoice} disabled={rebuildingInvoice} data-testid="rebuild-invoice-btn">
                          {rebuildingInvoice ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                          Rebuild from package
                        </Button>
                      </>
                    )}
                    {invoice.status === 'draft' && (
                      <Button
                        size="sm"
                        className="bg-[#C6A14A] hover:bg-[#d4af5a] text-black"
                        onClick={sendInvoice}
                        disabled={sendingInvoice}
                        data-testid="send-invoice-btn"
                      >
                        {sendingInvoice ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Send Invoice
                      </Button>
                    )}
                    {invoice.status === 'sent' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        onClick={markInvoicePaid}
                        data-testid="mark-invoice-paid-btn"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark Paid (Cash/Check)
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => window.open(`/invoice/${invoice.id}`, '_blank')}
                      data-testid="view-invoice-btn"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View Invoice
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-[#E8E4DE]/60 mb-4">No invoice created yet</p>
                  <Button
                    className="bg-[#C6A14A] hover:bg-[#d4af5a] text-black"
                    onClick={() => openInvoiceModal()}
                    data-testid="create-invoice-btn"
                  >
                    <DollarSign className="h-4 w-4 mr-1" />
                    Create Invoice
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Invoice Creation Modal */}
        <Dialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen}>
          <DialogContent className="bg-[#0f1724] border-[#C6A14A]/20 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-[#C6A14A]">{invoice ? 'Edit Draft Invoice' : 'Create Invoice'}</DialogTitle>
              <DialogDescription className="text-[#E8E4DE]/60">
                Configure invoice for {order?.title || 'this order'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Base Package Info */}
              <div className="bg-[#050816] rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#E8E4DE]">Base Package</span>
                  <span className="text-[#C6A14A] font-medium">
                    ${(((order?.details?.custom_quote_cents || order?.details?.package_price_cents || order?.details?.total_price_cents || order?.total_price_cents || 19900)) / 100).toFixed(2)}
                  </span>
                </div>
                <p className="text-[#E8E4DE]/50 text-sm mt-1">
                  {order?.service_type === 'vacant_land' ? `Vacant Land · ${order?.details?.land_band_label || ''}` : `${packageLabel(order?.details?.package || 'signature')} Package`}
                </p>
              </div>

              {/* Add-ons Section */}
              <div>
                <Label className="text-[#E8E4DE]/70 text-sm uppercase tracking-wider">Add-ons</Label>
                <div className="space-y-3 mt-2">
                  {availableAddons.length === 0 ? (
                    <p className="text-[#E8E4DE]/40 text-sm">Loading add-ons...</p>
                  ) : (
                    availableAddons.filter((a) => !INCLUDED_ADDON_KEYS.includes(a.key) || invoiceAddons[a.key]?.wasSelected).map((addon) => (
                      <div key={addon.key} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox
                            id={`addon-${addon.key}`}
                            data-testid={`invoice-addon-${addon.key}`}
                            checked={invoiceAddons[addon.key]?.selected || false}
                            onCheckedChange={(checked) => setInvoiceAddons(prev => ({
                              ...prev,
                              [addon.key]: { ...prev[addon.key], selected: checked }
                            }))}
                            className="border-[#C6A14A]/50 data-[state=checked]:bg-[#C6A14A] data-[state=checked]:border-[#C6A14A]"
                          />
                          <div className="flex-1">
                            <Label htmlFor={`addon-${addon.key}`} className="text-[#E8E4DE] cursor-pointer">
                              {addon.name}
                              {addon.key === 'ai_editing' && addon.price_cents === 2500 && (
                                <span className="ml-2 text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">
                                  PLACEHOLDER
                                </span>
                              )}
                            </Label>
                            {addon.description && (
                              <p className="text-xs text-[#E8E4DE]/50">{addon.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {addon.per_unit && invoiceAddons[addon.key]?.selected && (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="1"
                                value={invoiceAddons[addon.key]?.quantity || 1}
                                data-testid={`invoice-addon-qty-${addon.key}`}
                                onChange={(e) => setInvoiceAddons(prev => ({
                                  ...prev,
                                  [addon.key]: { ...prev[addon.key], quantity: parseInt(e.target.value, 10) || 1 }
                                }))}
                                className="w-16 h-8 text-center bg-[#050816] border-white/10 text-white"
                              />
                              <span className="text-[#E8E4DE]/50 text-xs">{addon.unit_label || 'units'}</span>
                            </div>
                          )}
                          <span className="text-[#C6A14A] whitespace-nowrap">
                            +${(addon.price_cents / 100).toFixed(2)}
                            {addon.per_unit && <span className="text-xs text-[#E8E4DE]/50">/{addon.unit_label || 'unit'}</span>}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Custom Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[#E8E4DE]/70 text-sm uppercase tracking-wider">Custom Line Items</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#C6A14A] hover:bg-[#C6A14A]/10"
                    onClick={addCustomItem}
                    data-testid="invoice-add-custom-item-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>
                {invoiceCustomItems.length > 0 ? (
                  <div className="space-y-2">
                    {invoiceCustomItems.map((item, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Input
                          placeholder="Description"
                          value={item.description}
                          data-testid={`invoice-custom-desc-${index}`}
                          onChange={(e) => updateCustomItem(index, 'description', e.target.value)}
                          className="flex-1 bg-[#050816] border-white/10 text-white"
                        />
                        <Input
                          type="number"
                          placeholder="Qty"
                          min="1"
                          value={item.quantity || 1}
                          data-testid={`invoice-custom-qty-${index}`}
                          onChange={(e) => updateCustomItem(index, 'quantity', e.target.value)}
                          className="w-16 bg-[#050816] border-white/10 text-white"
                        />
                        <Input
                          type="number"
                          placeholder="Price"
                          value={item.price}
                          data-testid={`invoice-custom-price-${index}`}
                          onChange={(e) => updateCustomItem(index, 'price', e.target.value)}
                          className="w-24 bg-[#050816] border-white/10 text-white"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-400 hover:bg-red-500/10"
                          onClick={() => removeCustomItem(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#E8E4DE]/40 text-sm">No custom items added</p>
                )}
              </div>

              {/* Notes */}
              <div>
                <Label className="text-[#E8E4DE]/70 text-sm uppercase tracking-wider">Notes (Optional)</Label>
                <Textarea
                  placeholder="Add any notes to appear on the invoice..."
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                  className="mt-2 bg-[#050816] border-white/10 text-white min-h-[80px]"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setInvoiceModalOpen(false)}
                className="border-white/20 text-[#E8E4DE]"
              >
                Cancel
              </Button>
              <Button
                className="bg-[#C6A14A] hover:bg-[#d4af5a] text-black"
                onClick={createInvoice}
                disabled={creatingInvoice}
                data-testid="create-invoice-submit-btn"
              >
                {creatingInvoice ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {invoice ? 'Save Draft' : 'Create Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Gallery Photos - Only show after 'shot' status */}
        {['shot', 'editing', 'delivered', 'paid'].includes(order.status) && (
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Camera className="h-5 w-5 text-[#C6A14A]" />
                    Gallery Photos
                  </CardTitle>
                  <CardDescription className="text-[#E8E4DE]/60">
                    {photos.length} photos • {order.gallery_slug ? `Slug: ${order.gallery_slug}` : 'No gallery yet'}
                  </CardDescription>
                </div>
                {photos.length > 0 && (
                  <Button size="sm" variant="outline" disabled={zipping}
                    onClick={async () => {
                      setZipping(true);
                      await startZipDownload(() => authFetch(`${API_BASE}/api/gallery/orders/${orderId}/zip-ticket`, { method: 'POST' }));
                      setZipping(false);
                    }}
                    className="border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="admin-download-zip-btn">
                    {zipping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    {zipping ? 'Preparing…' : 'Download All (ZIP)'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload Zone */}
              <DropZone
                onUpload={handlePhotoUpload}
                accept="image/*"
                multiple={true}
                disabled={uploading}
              />

              {/* Photos Grid */}
              {photosLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 text-[#C6A14A] animate-spin" />
                </div>
              ) : photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div
                      key={photo.id}
                      className="relative group aspect-[4/3] bg-[#050816]/50 rounded-lg overflow-hidden"
                    >
                      <img
                        src={photo.thumbnail_url || photo.preview_url}
                        alt={photo.filename}
                        className="w-full h-full object-cover"
                      />
                      {photo.is_cover && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-[#C6A14A] text-[#050816]">
                            <Star className="h-3 w-3 mr-1" />
                            Cover
                          </Badge>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {!photo.is_cover && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetCover(photo.id)}
                            className="text-white hover:text-[#C6A14A]"
                          >
                            <Star className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="text-white hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#E8E4DE]/50 py-8">
                  No photos uploaded yet
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Share Links & Portfolio */}
        {order.gallery_slug && (
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Share2 className="h-5 w-5 text-[#C6A14A]" />
                Links & Portfolio
              </CardTitle>
              <CardDescription className="text-[#E8E4DE]/60">
                Share links and public portfolio settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Link (Private, with token) */}
              <div className="space-y-3">
                <Label className="text-[#E8E4DE]/80 text-sm font-medium">Client Link (Private)</Label>
                <p className="text-[#E8E4DE]/50 text-xs">Watermarked preview with Pay button. Token required.</p>
                {shareUrl ? (
                  <div className="flex gap-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="bg-[#050816]/50 border-white/10 text-white font-mono text-xs"
                    />
                    <Button
                      onClick={handleCopyClientLink}
                      variant="outline"
                      className="border-[#C6A14A]/30 text-[#C6A14A] hover:bg-[#C6A14A]/10 shrink-0"
                      data-testid="copy-client-link-btn"
                    >
                      {clientLinkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span className="ml-2 hidden sm:inline">Copy Client Link</span>
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleGenerateShareLink}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Generate Share Link
                  </Button>
                )}
              </div>

              {/* Public Showcase Link (only for paid orders) */}
              {(order.status === 'paid' || order.payment_state === 'paid' || (order.status === 'delivered' && showInPortfolio)) && publicShowcaseUrl && (
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <Label className="text-[#E8E4DE]/80 text-sm font-medium">Public Share Link</Label>
                  <p className="text-[#E8E4DE]/50 text-xs" data-testid="public-link-hint">
                    Shows the cover photo on Facebook, Messenger & iMessage, then opens the property page.
                    {!showInPortfolio && ' Turn on "Add to Portfolio" below for this link to work.'}
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={publicShowcaseUrl}
                      readOnly
                      className="bg-[#050816]/50 border-white/10 text-white font-mono text-xs"
                    />
                    <Button
                      onClick={handleCopyPublicLink}
                      variant="outline"
                      className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 shrink-0"
                      data-testid="copy-public-link-btn"
                    >
                      {publicLinkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span className="ml-2 hidden sm:inline">Copy Public Link</span>
                    </Button>
                  </div>
                </div>
              )}

              {(order.status === 'paid' || order.payment_state === 'paid' || (order.status === 'delivered' && showInPortfolio)) && (
                <ShareLinkCard orderId={orderId} refreshKey={`${showInPortfolio}-${photos.length}-${photos.find(p => p.is_cover)?.id || ''}`} />
              )}

              {/* Portfolio Toggle - delivered or paid orders */}
              {(order.status === 'paid' || order.payment_state === 'paid' || order.status === 'delivered') && (
                <div className="pt-4 border-t border-white/10">
                  <div 
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      showInPortfolio 
                        ? 'bg-[#C6A14A]/10 border-[#C6A14A]/50' 
                        : 'bg-[#050816]/50 border-white/10 hover:border-white/20'
                    }`}
                    onClick={handleTogglePortfolio}
                    data-testid="portfolio-toggle"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${showInPortfolio ? 'bg-[#C6A14A]/20' : 'bg-white/5'}`}>
                          <Eye className={`h-5 w-5 ${showInPortfolio ? 'text-[#C6A14A]' : 'text-[#E8E4DE]/50'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${showInPortfolio ? 'text-[#C6A14A]' : 'text-white'}`}>
                            Add to Portfolio
                          </p>
                          <p className="text-[#E8E4DE]/50 text-xs">
                            {showInPortfolio 
                              ? 'Visible on public portfolio, homepage & listing website' 
                              : 'Show this property in the public portfolio'}
                          </p>
                          {order.status !== 'paid' && order.payment_state !== 'paid' && (
                            <p className="text-[#C6A14A]/80 text-xs mt-1" data-testid="portfolio-watermark-note">
                              Awaiting payment: the public sees watermarked photos until it's paid
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {portfolioSaving ? (
                          <Loader2 className="h-5 w-5 text-[#C6A14A] animate-spin" />
                        ) : (
                          <div className={`w-12 h-6 rounded-full transition-all ${
                            showInPortfolio ? 'bg-[#C6A14A]' : 'bg-white/10'
                          }`}>
                            <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform mt-0.5 ${
                              showInPortfolio ? 'translate-x-6' : 'translate-x-0.5'
                            }`} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Revoke Link Option */}
              {shareUrl && (
                <div className="pt-2">
                  <Button
                    onClick={handleRevokeShareLink}
                    variant="ghost"
                    size="sm"
                    className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10"
                  >
                    Revoke Client Link
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
